import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  useGetCategoriesQuery,
  useGetProductsQuery,
  useGetTagsQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ProductImage from "../CommonComponents/ProductImage";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";

import {
  Alert,
  CardBody,
  CardHeader,
  Container,
  Card,
  Row,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Tooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormFeedback,
  Spinner,
  CardFooter,
} from "reactstrap";
import ProductDetailsModal from "../Inventory/ProductDetailsModal";
import {
  useGetPurchaseRequestByIdQuery,
  useCreatePurchaseRequestItemsMutation,
  useUpdatePurchaseRequestMutation,
  useUpdatePurchaseRequestItemsMutation,
  useGetPurchaseRequestItemsByIdQuery,
  useDeletePurchaseRequestItemsByIdMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import ImportExcelModal from "../../../Components/Common/ImportExcelModal";
import "react-toastify/dist/ReactToastify.css";
import { useCheckPurchaseOrderAssignedWorkflowMutation } from "slices/workflow/WorkFlowSlice";
import { getErrorText } from "helpers/error_message";
import ValidationAlertStatic from "pages/SafetyApp/ValidationAlertStatic";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";
import PurchaseRequestModal from "./Components/PurchaseRequestModal";

// Skeleton placeholder shown in the product grid while products / filters load
const ProductCardSkeleton = () => (
  <Card className="h-100 product-card">
    <CardBody className="p-4 d-flex flex-column placeholder-glow">
      <div className="text-center mb-3">
        <div
          className="placeholder rounded w-100 bg-light"
          style={{ height: "150px" }}
        ></div>
      </div>
      <div className="mb-3" style={{ minHeight: "48px" }}>
        <span className="placeholder col-9 mb-1"></span>
        <span className="placeholder col-6"></span>
      </div>
      <div className="d-flex align-items-center mb-2">
        <span className="placeholder col-4"></span>
      </div>
      <div className="d-flex justify-content-between mb-3">
        <span className="placeholder col-4 rounded-pill"></span>
        <span className="placeholder col-3 rounded-pill"></span>
      </div>
      <div className="mt-auto">
        <span
          className="placeholder w-100 rounded"
          style={{ height: "38px", display: "block" }}
        ></span>
      </div>
    </CardBody>
  </Card>
);

interface Product {
  id: number;
  name: string;
  description: string;
  brand_company: string;
  model_number: string;
  thumbnail_url: string | null;
  attachment_urls: Array<{
    id: number;
    name: string;
    file: string;
    file_url: string;
    description: string | null;
  }> | null;
  category_name: string;
  measurement_name: string;
  tag_names: string[];
  attributes?: Record<string, any> | null;
  project_assignment_id?: number | null;
  project_maximum_quantity?: string | number | null;
  project_requested_quantity?: string | number | null;
  project_remaining_quantity?: string | number | null;
  already_added_to_purchase_request?: boolean;
  created_at: string;
  modified_at: string;
}

// Normalize product attributes to a plain object (API returns an object, but be
// defensive if it ever arrives as a JSON string).
const parseAttributes = (attrs: any): Record<string, any> => {
  if (!attrs) return {};
  if (typeof attrs === "string") {
    try {
      const parsed = JSON.parse(attrs);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof attrs === "object" ? attrs : {};
};

interface CartItem {
  id?: number;
  product: Product;
  quantity: number;
  expected_delivery_date: string;
  delivery_note: string;
  delivery_area: string;
  remark: string;
  pr_model_number: string;
  isEditing?: boolean;
  is_po_created?: boolean; // Flag to indicate if PO is already created for this item
}

interface PurchaseRequestModalData {
  summary: string;
  purchase_request_number: string;
  status: string;
  expected_delivery_date: string;
  delivery_address: string;
  delivery_note: string;
  project: number;
  request_submitted_date: string;
}

interface AddToCartModalData {
  product: Product | null;
  quantity: number;
  expected_delivery_date: string;
  delivery_note: string;
  delivery_area: string;
  remark: string;
  pr_model_number: string;
  isEditing: boolean;
  editingItemId?: number;
}

const toNumber = (value: unknown): number => {
  const num = parseFloat(String(value ?? 0));
  return Number.isFinite(num) ? num : 0;
};

const extractApiErrorMessage = (error: any): string => {
  if (typeof error?.data?.detail === "string") return error.data.detail;
  if (typeof error?.data?.error === "string") return error.data.error;
  if (typeof error?.error === "string") return error.error;
  if (error?.data && typeof error.data === "object") {
    for (const value of Object.values(error.data)) {
      if (Array.isArray(value) && value.length > 0) return String(value[0]);
      if (typeof value === "string") return value;
    }
  }
  return "Please try again.";
};

const PurchaseRequest = () => {
  const { purchaseId } = useParams<{ purchaseId: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [tooltipOpen, setTooltipOpen] = useState<Record<number, boolean>>({});
  const [purchaseRequestModalOpen, setPurchaseRequestModalOpen] =
    useState(false);
  const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<CartItem | null>(null);
  const [currentUserEmployeeId, setCurrentUserEmployeeId] = useState<
    number | null
  >(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [isImportExcel, setIsImportExcel] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [modalBackDrop, setModalBackDrop] = useState<boolean>(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const [checkSafteyAppAssignedWorkflow] =
    useCheckPurchaseOrderAssignedWorkflowMutation();

  const [addToCartModalData, setAddToCartModalData] =
    useState<AddToCartModalData>({
      product: null,
      quantity: 1,
      expected_delivery_date: moment().add(7, "days").format("YYYY-MM-DD"),
      delivery_note: "",
      delivery_area: "",
      remark: "",
      pr_model_number: "",
      isEditing: false,
      editingItemId: undefined,
    });

  const [purchaseRequestData, setPurchaseRequestData] =
    useState<PurchaseRequestModalData>({
      summary: "",
      purchase_request_number: "",
      status: "submitted",
      expected_delivery_date: moment().format("YYYY-MM-DD"),
      delivery_address: "",
      delivery_note: "",
      project: 0,
      request_submitted_date: moment().format("YYYY-MM-DD"),
    });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [cartFormErrors, setCartFormErrors] = useState<Record<string, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    status: number;
    search?: string;
    category?: string;
    tag?: string;
    project?: string;
    purchase_request?: string;
  }>({
    page: 1,
    pageSize: 25,
    status: 2,
    search: "",
    category: "",
    tag: "",
    project: "",
    purchase_request: purchaseId || "",
  });

  const {
    data: productsData,
    isLoading,
    isFetching,
    refetch: refetchProducts,
    error,
  } = useGetProductsQuery(page);

  const {
    data: existingPurchaseRequest,
    isLoading: isLoadingPurchaseRequest,
    refetch: refetchExistingPurchaseRequest,
  } = useGetPurchaseRequestByIdQuery(purchaseId ? parseInt(purchaseId) : 0, {
    skip: !purchaseId,
  });

  const { data: categoriesData } = useGetCategoriesQuery(undefined);
  const { data: tagsData } = useGetTagsQuery(undefined);
  const { data: projectsData } = useGetProjectsByDropDownQuery(undefined);

  const tagOptions = React.useMemo(
    () =>
      (tagsData?.results || []).map((t: any) => ({
        value: String(t.id),
        label: t.name,
      })),
    [tagsData]
  );

  const [createPurchaseRequestItems] = useCreatePurchaseRequestItemsMutation();
  const [updatePurchaseRequest] = useUpdatePurchaseRequestMutation();
  const [updatePurchaseRequestItems] = useUpdatePurchaseRequestItemsMutation();
  const [deletePurchaseRequestItems] =
    useDeletePurchaseRequestItemsByIdMutation();

  useEffect(() => {
    if (productsData && productsData.results) {
      setProducts(productsData.results);
    }
  }, [productsData]);

  useEffect(() => {
    const fetchAuthUser = () => {
      const authUserString = localStorage.getItem("authUser");
      if (authUserString) {
        const authUser = JSON.parse(authUserString);
        setCurrentUserEmployeeId(authUser?.employee_id || null);
      }
    };
    fetchAuthUser();
  }, []);

  // Load existing purchase request items into cart when editing
  useEffect(() => {
    if (existingPurchaseRequest && existingPurchaseRequest.items) {
      const cartItemsFromRequest = existingPurchaseRequest.items.map(
        (item: any) => ({
          id: item.id,
          product: {
            id: item.product,
            name: item.product_name,
            description: "",
            brand_company: "",
            model_number: "",
            attachment_urls: item.attachment_urls || [],
            thumbnail_url: item.thumbnail_url,
            category_name: item.category_name,
            measurement_name: item.measurement_name,
            tag_names: item.tag_names || [],
            project_assignment_id: item.project_assignment_id,
            project_maximum_quantity: item.project_maximum_quantity,
            project_requested_quantity: item.project_requested_quantity,
            project_remaining_quantity: item.project_remaining_quantity,
            already_added_to_purchase_request: true,
            created_at: item.created_at,
            modified_at: item.modified_at,
          },
          quantity: parseInt(item.quantity) || 1,
          expected_delivery_date: item.expected_delivery_date
            ? moment(item.expected_delivery_date).format("YYYY-MM-DD")
            : moment().add(7, "days").format("YYYY-MM-DD"),
          delivery_note: item.delivery_note || "",
          delivery_area: item.delivery_area || "",
          remark: item.remark || "",
          pr_model_number: item.pr_model_number || "",
          is_po_created: item.is_po_created || false,
        })
      );
      setCartItems(cartItemsFromRequest);

      setPurchaseRequestData({
        summary: existingPurchaseRequest.summary || "",
        purchase_request_number:
          existingPurchaseRequest.purchase_request_number || "",
        status: existingPurchaseRequest.status || "submitted",
        expected_delivery_date: existingPurchaseRequest.expected_delivery_date
          ? moment(existingPurchaseRequest.expected_delivery_date).format(
              "YYYY-MM-DD",
            )
          : moment().format("YYYY-MM-DD"),
        delivery_address:
          existingPurchaseRequest.delivery_address ||
          existingPurchaseRequest.delivery_note ||
          "",
        delivery_note: existingPurchaseRequest.delivery_note || "",
        project: existingPurchaseRequest.project || 0,
        request_submitted_date: existingPurchaseRequest.request_submitted_date
          ? moment(existingPurchaseRequest.request_submitted_date).format(
              "YYYY-MM-DD",
            )
          : moment().format("YYYY-MM-DD"),
      });

      if (existingPurchaseRequest.project) {
        setSelectedProject(existingPurchaseRequest.project);
      }
    }
  }, [existingPurchaseRequest]);

  const getProjectNameById = (projectId: number) => {
    if (!projectsData || !projectsData.results) return "";
    const project = projectsData.results.find((p: any) => p.id === projectId);
    return project ? project.project_name : "";
  };

  const checkWorkflowProject = async () => {
    if (!selectedProject || selectedProject === 0) {
      setValidationMessages([]);
      return;
    }

    try {
      let assignedWorkflow: any = await checkSafteyAppAssignedWorkflow({
        reportKey: "PURCHASE_ORDER",
        projectId: selectedProject,
      });

      if (assignedWorkflow.data.length === 0) {
        const projectName = getProjectNameById(selectedProject);
        const errorMessageText = `${getErrorText("workflow-not-assigned")} ${projectName}`;

        setValidationMessages((prev: any) => {
          if (prev.some((message: any) => message.key === errorMessageText)) {
            return prev;
          }
          const errorMessage = (
            <span key={errorMessageText}>
              {getErrorText("workflow-not-assigned")} <b>{projectName}</b>
            </span>
          );
          return [...prev, errorMessage];
        });

        setModalBackDrop(true);
      } else {
        setValidationMessages([]);
        setModalBackDrop(false);
      }
    } catch (error) {
      console.error("Error checking workflow:", error);
    }
  };

  useEffect(() => {
    setValidationMessages([]);
    if (selectedProject && selectedProject !== 0) {
      checkWorkflowProject();
    }
  }, [selectedProject]);

  const toggleModal = () => setModalOpen(!modalOpen);
  const togglePurchaseRequestModal = () =>
    setPurchaseRequestModalOpen(!purchaseRequestModalOpen);
  const toggleAddToCartModal = () => {
    setAddToCartModalOpen(!addToCartModalOpen);
    if (!addToCartModalOpen) {
      setCartFormErrors({});
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPageState((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setPageState((prev) => ({ ...prev, category: categoryId, page: 1 }));
  };

  const displayedProducts = products;

  useEffect(() => {
    if (productsData && productsData.results) {
      setProducts(productsData.results);
    } else {
      setProducts([]);
    }
  }, [productsData]);

  useEffect(() => {
    setPageState((prev) => ({
      ...prev,
      page: 1,
      project: selectedProject ? String(selectedProject) : "",
      purchase_request: purchaseId || "",
    }));
  }, [selectedProject, purchaseId]);

  const toggleDeleteConfirmModal = () =>
    setDeleteConfirmModalOpen(!deleteConfirmModalOpen);

  const toggleTooltip = (productId: number) => {
    setTooltipOpen((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
  };

  const openProductModal = (productId: number) => {
    setSelectedProductId(productId);
    setModalOpen(true);
  };

  const openAddToCartModal = (
    product: Product,
    isEditing: boolean = false,
    itemId?: number,
    existingItem?: CartItem,
  ) => {
    setAddToCartModalData({
      product,
      quantity: existingItem ? existingItem.quantity : 1,
      expected_delivery_date: existingItem
        ? existingItem.expected_delivery_date
        : moment().add(7, "days").format("YYYY-MM-DD"),
      delivery_note: existingItem ? existingItem.delivery_note : "",
      delivery_area: existingItem ? existingItem.delivery_area : "",
      remark: existingItem ? existingItem.remark : "",
      // Sync PR model number from the product's model number when it has one (locked);
      // otherwise let the user enter it (or keep the existing cart value when editing).
      pr_model_number: product?.model_number
        ? product.model_number
        : existingItem
          ? existingItem.pr_model_number
          : "",
      isEditing,
      editingItemId: itemId,
    });
    setAddToCartModalOpen(true);
  };

  const getAllowedQuantity = (
    product?: Product | null,
    existingItem?: CartItem,
  ) => {
    if (!product) return 0;
    const remaining = toNumber(product.project_remaining_quantity);
    const currentItemQty = existingItem ? toNumber(existingItem.quantity) : 0;
    return existingItem ? remaining + currentItemQty : remaining;
  };

  const openDeleteConfirmModal = (item: CartItem) => {
    setItemToDelete(item);
    setDeleteConfirmModalOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete || !itemToDelete.id) return;

    setIsSubmitting(true);

    try {
      if (itemToDelete.id) {
        await deletePurchaseRequestItems(itemToDelete.id).unwrap();
      }

      setCartItems((prev) =>
        prev.filter((item) => item?.id !== itemToDelete.id),
      );

      toast.success(`${itemToDelete.product.name} removed from cart`, {
        position: "top-right",
        autoClose: 3000,
      });

      setDeleteConfirmModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error(
        `Failed to remove ${itemToDelete.product.name}. Please try again.`,
        {
          position: "top-right",
          autoClose: 5000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToCartChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setAddToCartModalData((prev) => ({
      ...prev,
      [name]: name === "quantity" ? parseInt(value) || 1 : value,
    }));

    if (cartFormErrors[name]) {
      setCartFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateCartForm = (): boolean => {
    const errors: Record<string, string> = {};
    const existingItem = addToCartModalData.isEditing
      ? cartItems.find((item) => item.id === addToCartModalData.editingItemId)
      : undefined;
    const allowedQuantity = getAllowedQuantity(
      addToCartModalData.product,
      existingItem,
    );

    if (!addToCartModalData.quantity || addToCartModalData.quantity < 1) {
      errors.quantity = "Quantity must be at least 1";
    } else if (
      allowedQuantity > 0 &&
      addToCartModalData.quantity > allowedQuantity
    ) {
      errors.quantity = `Quantity cannot exceed the assigned project limit. Available quantity: ${allowedQuantity.toFixed(2)}.`;
    }

    if (!addToCartModalData.expected_delivery_date) {
      errors.expected_delivery_date = "Expected delivery date is required";
    } else if (
      !moment(
        addToCartModalData.expected_delivery_date,
        "YYYY-MM-DD",
        true,
      ).isValid()
    ) {
      errors.expected_delivery_date = "Invalid date format";
    } else if (
      moment(addToCartModalData.expected_delivery_date).isBefore(
        moment(),
        "day",
      )
    ) {
      errors.expected_delivery_date =
        "Expected delivery date cannot be in the past";
    }

    if (!addToCartModalData.delivery_note.trim()) {
      errors.delivery_note = "Delivery note is required";
    }

    if (!addToCartModalData.delivery_area.trim()) {
      errors.delivery_area = "Delivery area is required";
    }

    setCartFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddToCartSubmit = async () => {
    if (!validateCartForm() || !addToCartModalData.product) return;

    setIsSubmitting(true);

    try {
      if (addToCartModalData.isEditing && addToCartModalData.editingItemId) {
        let productData = {
          quantity: addToCartModalData.quantity.toString(),
          expected_delivery_date: addToCartModalData.expected_delivery_date,
          delivery_note: addToCartModalData.delivery_note,
          delivery_area: addToCartModalData.delivery_area,
          remark: addToCartModalData.remark,
          pr_model_number: addToCartModalData.pr_model_number,
        };

        await updatePurchaseRequestItems({
          id: addToCartModalData.editingItemId,
          productData: productData,
        }).unwrap();

        setCartItems((prev) =>
          prev.map((item) =>
            item.id === addToCartModalData.editingItemId
              ? {
                  ...item,
                  quantity: addToCartModalData.quantity,
                  expected_delivery_date:
                    addToCartModalData.expected_delivery_date,
                  delivery_note: addToCartModalData.delivery_note,
                  delivery_area: addToCartModalData.delivery_area,
                  remark: addToCartModalData.remark,
          pr_model_number: addToCartModalData.pr_model_number,
                }
              : item,
          ),
        );

        toast.success("Item updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        if (purchaseId) {
          const newItemPayload = {
            purchase_request: parseInt(purchaseId),
            product: addToCartModalData.product.id,
            quantity: addToCartModalData.quantity.toString(),
            expected_delivery_date: addToCartModalData.expected_delivery_date,
            delivery_note: addToCartModalData.delivery_note,
            delivery_area: addToCartModalData.delivery_area,
            remark: addToCartModalData.remark,
          pr_model_number: addToCartModalData.pr_model_number,
          };

          const response =
            await createPurchaseRequestItems(newItemPayload).unwrap();

          const newCartItem: CartItem = {
            id: response.id,
            product: addToCartModalData.product,
            quantity: addToCartModalData.quantity,
            expected_delivery_date: addToCartModalData.expected_delivery_date,
            delivery_note: addToCartModalData.delivery_note,
            delivery_area: addToCartModalData.delivery_area,
            remark: addToCartModalData.remark,
          pr_model_number: addToCartModalData.pr_model_number,
          };

          setCartItems((prev) => [...prev, newCartItem]);
        } else {
          const newCartItem: CartItem = {
            product: addToCartModalData.product,
            quantity: addToCartModalData.quantity,
            expected_delivery_date: addToCartModalData.expected_delivery_date,
            delivery_note: addToCartModalData.delivery_note,
            delivery_area: addToCartModalData.delivery_area,
            remark: addToCartModalData.remark,
          pr_model_number: addToCartModalData.pr_model_number,
          };

          setCartItems((prev) => [...prev, newCartItem]);
        }

        toast.success(`${addToCartModalData.product.name} added to cart!`, {
          position: "top-right",
          autoClose: 3000,
        });
      }

      setAddToCartModalOpen(false);
    } catch (error) {
      console.error("Failed to add/update cart item:", error);
      toast.error(
        `Failed to ${addToCartModalData.isEditing ? "update" : "add"} item. ${extractApiErrorMessage(error)}`,
        { position: "top-right", autoClose: 5000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePurchaseRequestChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setPurchaseRequestData((prev) => ({ ...prev, [name]: value }));
    if (name === "project") {
      setSelectedProject(parseInt(value || "0", 10) || 0);
    }

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!purchaseRequestData.summary.trim()) {
      errors.summary = "Summary is required";
    }

    if (!purchaseRequestData.expected_delivery_date) {
      errors.expected_delivery_date = "Expected delivery date is required";
    } else if (
      !moment(
        purchaseRequestData.expected_delivery_date,
        "YYYY-MM-DD",
        true,
      ).isValid()
    ) {
      errors.expected_delivery_date = "Invalid date format";
    } else if (
      moment(purchaseRequestData.expected_delivery_date).isBefore(
        moment(),
        "day",
      )
    ) {
      errors.expected_delivery_date =
        "Expected delivery date cannot be in the past";
    }

    if (!purchaseRequestData.delivery_address.trim()) {
      errors.delivery_address = "Delivery address is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Main submit handler (called from PurchaseRequestModal Step 2) ──────────
  const handleSubmitPurchaseRequest = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Determine status: if current status is "rejected", set to "resubmit", otherwise "submitted"
      const currentStatus = purchaseRequestData.status?.toLowerCase();
      const newStatus = currentStatus === "rejected" ? "resubmit" : "submitted";

      // Prepare the purchase request payload with proper date formatting using moment
      const productData = {
        summary: purchaseRequestData.summary,
        purchase_request_number: purchaseRequestData.purchase_request_number,
        status: newStatus,
        expected_delivery_date: purchaseRequestData.expected_delivery_date,
        delivery_address: purchaseRequestData.delivery_address,
        delivery_note: purchaseRequestData.delivery_note,
        project: purchaseRequestData.project,
        request_submitted_date: moment().toISOString(),
        submitted_by: currentUserEmployeeId,
      };

      if (purchaseId) {
        await updatePurchaseRequest({
          id: parseInt(purchaseId),
          productData: productData,
        }).unwrap();

        const successMessage = newStatus === "resubmit" 
          ? "Purchase request resubmitted successfully!" 
          : "Purchase request submitted successfully!";

        toast.success(successMessage, {
          position: "top-right",
          autoClose: 3000,
        });

        setPurchaseRequestModalOpen(false);

        setTimeout(() => {
          navigate("/purchase-request-list");
        }, 1500);
      } else {
        toast.error(
          "Creating new purchase requests requires additional implementation",
        );
      }
    } catch (error) {
      console.error("Failed to submit purchase request:", error);
      toast.error("Failed to submit purchase request. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const term = searchTerm.toLowerCase();
    // Attribute text = "key value" pairs joined, so both keys and values match.
    const attributesText = Object.entries(parseAttributes(product.attributes))
      .map(([k, v]) => `${k} ${String(v)}`)
      .join(" ")
      .toLowerCase();
    const tagsText = (product.tag_names || []).join(" ").toLowerCase();

    const matchesSearch =
      product.name.toLowerCase().includes(term) ||
      product.brand_company.toLowerCase().includes(term) ||
      (product.description || "").toLowerCase().includes(term) ||
      (product.model_number || "").toLowerCase().includes(term) ||
      tagsText.includes(term) ||
      attributesText.includes(term);

    const matchesCategory =
      selectedCategory === "All" || product.category_name === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const removeCartItem = (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    setCartItems(cartItems.filter((item) => item.id !== id));

    if (item) {
      toast.info(`${item.product.name} removed from cart`, {
        position: "top-right",
        autoClose: 2000,
      });
    }
  };

  const totalResults = productsData?.count || 0;
  const totalPages = Math?.ceil(totalResults / page.pageSize);
  const hasProjectSelection = !!selectedProject;

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPageState((prev) => ({ ...prev, page: newPage }));
  };

  // Products loading is handled with skeleton cards in the grid (so the filter
  // bar stays usable while a search/category/tag filter loads); only block the
  // whole page while the purchase request itself is loading.
  if (purchaseId && isLoadingPurchaseRequest) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Inventory" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Spinner color="primary" className="me-2" />
            <span>Loading purchase request...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Inventory" pageTitle="Resource Inventory" />
          <div className="text-center py-5 text-danger">
            <i className="ri-error-warning-line display-4"></i>
            <p className="mt-3">
              Error loading products. Please try again later.
            </p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={
              purchaseId ? "Edit Purchase Request" : "Create Purchase Request"
            }
            pageTitle="Resource Inventory"
          />

          <ImportExcelModal
            show={isImportExcel}
            onCloseClick={() => setIsImportExcel(false)}
            import_type={"purchase_request_import"}
            refetch={refetchExistingPurchaseRequest}
            meta={purchaseId}
          />

          <Card className="border-primary">
            <CardHeader className="border-0 rounded bg-light-primary">
              <Row className="g-2">
                <Col xl={3}>
                  <div className="search-box">
                    <Input
                      type="text"
                      className="form-control search border-primary"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    <i className="ri-search-line search-icon text-primary"></i>
                  </div>
                </Col>
                <Col xxl={2} className="ms-auto">
                  <select
                    className="form-control border-primary"
                    value={page.category}
                    onChange={handleCategoryChange}
                  >
                    <option value="">Select Category</option>
                    {categoriesData?.results?.map((item: any) => (
                      <option key={item.id} value={item.id.toString()}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col xxl={2}>
                  <Select
                    classNamePrefix="select"
                    isClearable
                    placeholder="Select Tag..."
                    options={tagOptions}
                    value={
                      tagOptions.find(
                        (o: any) => o.value === (page.tag || "")
                      ) || null
                    }
                    onChange={(opt: any) =>
                      setPageState((prev) => ({
                        ...prev,
                        tag: opt ? opt.value : "",
                        page: 1,
                      }))
                    }
                  />
                </Col>
                <Col xxl={3} className="ms-auto">
                  <a
                    type="button"
                    className="btn btn-secondary"
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/products/?export=[name,model_number,item_group_code,item_type,measurement__name]&type=csv&filename=inventory_export`}
                  >
                    <i className="ri-file-download-line align-bottom me-1"></i>
                    Export Products
                  </a>
                </Col>

                <div className="col-lg-auto">
                  <div className="hstack gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary w-100"
                      onClick={() => setIsImportExcel(true)}
                    >
                      <i className="ri-file-download-line align-bottom me-1"></i>
                      Import
                    </button>
                  </div>
                </div>
              </Row>
            </CardHeader>
          </Card>

          <Row className="mt-4">
            {/* Products Grid */}
            <Col xl={9}>
              <Row>
                {(isLoading || isFetching) &&
                  Array.from({ length: 6 }).map((_, idx) => (
                    <Col lg={4} md={6} key={`skeleton-${idx}`} className="mb-4">
                      <ProductCardSkeleton />
                    </Col>
                  ))}
                {!isLoading &&
                  !isFetching &&
                  !hasProjectSelection && (
                    <Col xs={12}>
                      <Alert color="info" className="mb-0">
                        Select a project for this purchase request to load only the assigned products.
                      </Alert>
                    </Col>
                  )}
                {!isLoading &&
                  !isFetching &&
                  hasProjectSelection &&
                  (!filteredProducts || filteredProducts.length === 0) && (
                    <Col xs={12}>
                      <div className="text-center py-5">
                        <i className="ri-inbox-line display-4 text-muted opacity-50"></i>
                        <h5 className="mt-3 text-muted">
                          No products available
                        </h5>
                        <p className="text-muted mb-0 fs-13">
                          Try adjusting the search, category or tag filters.
                        </p>
                      </div>
                    </Col>
                  )}
                {!isLoading &&
                  !isFetching &&
                  hasProjectSelection &&
                  filteredProducts?.map((product) => (
                  <Col lg={4} md={6} key={product.id} className="mb-4">
                    <Card className="h-100 product-card">
                      <CardBody className="p-4 d-flex flex-column">
                        <div className="text-center mb-3">
                          <ProductImage
                            attachment_urls={product.attachment_urls}
                            alt={product.name}
                            className="mb-3"
                            style={{
                              maxHeight: "150px",
                              minHeight: "150px",
                              objectFit: "contain",
                            }}
                          />
                        </div>

                        <div className="mb-3" style={{ minHeight: "48px" }}>
                          {product.name ? (
                            <p className="text-primary fw-semibold mb-0">
                              {product.name.length > 500
                                ? `${product.name.substring(0, 500)}...`
                                : product.name}
                            </p>
                          ) : (
                            <p className="text-muted small mb-0 fst-italic">
                              No Product available
                            </p>
                          )}
                        </div>

                        <div className="d-flex align-items-center mb-2">
                          <p className="text-info mb-0 fw-semibold">
                            <i className="ri-building-2-line me-1"></i>
                            {product.brand_company}
                          </p>
                          <span
                            id={`info-icon-${product.id}`}
                            className="ms-2 text-primary cursor-pointer"
                            onClick={() => openProductModal(product.id)}
                          >
                            <i className="ri-information-line"></i>
                          </span>
                          <Tooltip
                            placement="top"
                            isOpen={tooltipOpen[product.id] || false}
                            target={`info-icon-${product.id}`}
                            toggle={() => toggleTooltip(product.id)}
                          >
                            View product details
                          </Tooltip>
                        </div>

                        <div className="d-flex justify-content-between small text-muted mb-2">
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                            <i className="ri-price-tag-3-line me-1"></i>
                            {product.category_name}
                          </span>
                          <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">
                            <i className="ri-ruler-line me-1"></i>
                            {product.measurement_name}
                          </span>
                        </div>

                        {/* Tags */}
                        {product.tag_names && product.tag_names.length > 0 && (
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {product.tag_names.map((tag, tIdx) => (
                              <span
                                key={tIdx}
                                className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25"
                              >
                                <i className="ri-hashtag me-1"></i>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Product Attributes (key: value pairs) */}
                        {Object.keys(parseAttributes(product.attributes)).length > 0 && (
                          <div className="mb-3 p-2 bg-light rounded">
                            <div className="d-flex flex-wrap gap-x-3 gap-y-1">
                              {Object.entries(parseAttributes(product.attributes)).map(
                                ([key, val], aIdx) => (
                                  <span key={aIdx} className="small text-muted me-2">
                                    <span className="fw-semibold text-body">{key}:</span>{" "}
                                    {String(val)}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mb-3 p-2 border rounded bg-light-subtle">
                          <div className="small text-muted d-flex justify-content-between">
                            <span>Maximum Qty</span>
                            <span className="fw-semibold text-dark">
                              {product.project_maximum_quantity ?? "—"}
                            </span>
                          </div>
                          <div className="small text-muted d-flex justify-content-between">
                            <span>Requested Qty</span>
                            <span className="fw-semibold text-dark">
                              {product.project_requested_quantity ?? "—"}
                            </span>
                          </div>
                          <div className="small text-muted d-flex justify-content-between">
                            <span>Remaining Qty</span>
                            <span
                              className={`fw-semibold ${
                                toNumber(product.project_remaining_quantity) > 0
                                  ? "text-success"
                                  : "text-danger"
                              }`}
                            >
                              {product.project_remaining_quantity ?? "—"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-auto">
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => openAddToCartModal(product, false)}
                            disabled={
                              !hasProjectSelection ||
                              !!product.already_added_to_purchase_request ||
                              toNumber(product.project_remaining_quantity) <= 0
                            }
                          >
                            <i className="ri-shopping-cart-line me-1 align-bottom"></i>
                            {product.already_added_to_purchase_request
                              ? "Already Added"
                              : toNumber(product.project_remaining_quantity) <= 0
                              ? "Limit Reached"
                              : "Add to Cart"}
                          </button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {totalPages > 1 && (
                <Pagination className="justify-content-center mt-3">
                  <PaginationItem disabled={page.page === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(page.page - 1)}
                    />
                  </PaginationItem>

                  <PaginationItem active={page.page === 1}>
                    <PaginationLink onClick={() => handlePageChange(1)}>
                      1
                    </PaginationLink>
                  </PaginationItem>

                  {page.page > 3 && (
                    <PaginationItem disabled>
                      <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                  )}

                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const shouldShow =
                      pageNumber === page.page ||
                      pageNumber === page.page - 1 ||
                      pageNumber === page.page + 1 ||
                      (page.page >= totalPages - 2 &&
                        pageNumber >= totalPages - 2 &&
                        pageNumber <= totalPages);

                    if (pageNumber === 1) return null;
                    if (!shouldShow) return null;

                    return (
                      <PaginationItem
                        key={pageNumber}
                        active={page.page === pageNumber}
                      >
                        <PaginationLink
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  {page.page < totalPages - 2 && (
                    <PaginationItem disabled>
                      <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                  )}

                  {totalPages > 3 && page.page <= totalPages - 3 && (
                    <PaginationItem active={page.page === totalPages}>
                      <PaginationLink
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem disabled={page.page === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(page.page + 1)}
                    />
                  </PaginationItem>
                </Pagination>
              )}
            </Col>

            {/* Cart Section */}
            <Col xl={3}>
              <div className="sticky-side-div">
                <Card className="border-success">
                  <CardHeader className="border-bottom-dashed bg-light-success">
                    <h5 className="card-title mb-0 text-success">
                      <i className="ri-shopping-cart-2-line me-2 align-middle"></i>
                      Purchase Cart ({cartItems.length} items)
                    </h5>
                  </CardHeader>

                  <CardBody style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {cartItems.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="ri-shopping-cart-line display-4 text-muted"></i>
                        <p className="text-muted mt-2">Your cart is empty</p>
                      </div>
                    ) : (
                      <div className="cart-items">
                        {cartItems?.map((item) => (
                          <div
                            key={item.id || `temp-${item.product.id}`}
                            className="border-bottom pb-3 mb-3"
                          >
                            <div className="d-flex align-items-start">
                              <ProductThumbnail
                                attachment_urls={item.product.attachment_urls}
                                alt={item.product.name}
                                size="md"
                                className="me-2"
                              />
                              <div className="flex-grow-1">
                                <h6 className="mb-0 text-dark">
                                  {item.product.name}
                                </h6>
                                <p className="text-muted small mb-0">
                                  <i className="ri-ruler-line me-1"></i>
                                  {item.product.measurement_name}
                                </p>
                                <p className="text-info small mb-0 fw-semibold">
                                  Qty: {item.quantity}
                                </p>
                                <p className="text-warning small mb-0">
                                  Delivery:{" "}
                                  {moment(item.expected_delivery_date).format(
                                    "MMM DD, YYYY",
                                  )}
                                </p>
                              </div>

                              <div className="d-flex flex-column align-items-center">
                                <button
                                  type="button"
                                  className={`btn btn-link p-0 mb-1 ${item.is_po_created ? 'text-muted' : 'text-primary'}`}
                                  onClick={() =>
                                    !item.is_po_created && openAddToCartModal(
                                      item.product,
                                      true,
                                      item.id,
                                      item,
                                    )
                                  }
                                  title={item.is_po_created ? "Cannot edit - PO already created" : "Edit item"}
                                  disabled={item.is_po_created || isSubmitting}
                                >
                                  <i className="ri-edit-line"></i>
                                </button>
                                <button
                                  type="button"
                                  className={`btn btn-link p-0 ${item.is_po_created ? 'text-muted' : 'text-danger'}`}
                                  onClick={() => !item.is_po_created && openDeleteConfirmModal(item)}
                                  title={item.is_po_created ? "Cannot delete - PO already created" : "Remove item"}
                                  disabled={item.is_po_created || isSubmitting}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardBody>

                  <CardFooter>
                    {cartItems.length > 0 && (
                      <div className="mt-4">
                        <button
                          className="btn btn-success w-100"
                          onClick={togglePurchaseRequestModal}
                          disabled={cartItems.length === 0 || isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              {purchaseId ? "Updating..." : "Submitting..."}
                            </>
                          ) : (
                            <>
                              <i className="ri-check-line me-1 align-bottom"></i>
                              {purchaseId
                                ? "Update Purchase Request"
                                : "Proceed to Requisition"}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </CardFooter>
                </Card>
              </div>
            </Col>
          </Row>

          <ValidationAlertStatic
            modalBackDrop={modalBackDrop}
            setModalBackDrop={setModalBackDrop}
            validationMessages={validationMessages}
            previousPageNavLink="/purchase-request-list"
          />

          {/* Product Details Modal */}
          {selectedProductId && (
            <ProductDetailsModal
              isOpen={modalOpen}
              toggle={closeModal}
              productId={selectedProductId}
            />
          )}

          {/* Add to Cart Modal */}
          <Modal isOpen={addToCartModalOpen} toggle={toggleAddToCartModal}>
            <ModalHeader toggle={toggleAddToCartModal}>
              {addToCartModalData.isEditing ? "Edit Cart Item" : "Add to Cart"}
            </ModalHeader>
            <ModalBody>
              {addToCartModalData.product && (
                <>
                  <div className="mb-3">
                    <Label className="form-label">Product</Label>
                    <div className="d-flex align-items-center">
                      <ProductThumbnail
                        attachment_urls={
                          addToCartModalData.product?.attachment_urls
                        }
                        alt={addToCartModalData.product?.name || ""}
                        size="sm"
                        className="me-2"
                      />
                      <div>
                        <h6 className="mb-0">
                          {addToCartModalData.product.name}
                        </h6>
                        <p className="text-muted small mb-0">
                          {addToCartModalData.product.category_name}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="quantity" className="form-label">
                      Quantity <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      id="quantity"
                      name="quantity"
                      value={addToCartModalData.quantity}
                      onChange={handleAddToCartChange}
                      invalid={!!cartFormErrors.quantity}
                      min="1"
                      max={getAllowedQuantity(
                        addToCartModalData.product,
                        addToCartModalData.isEditing
                          ? cartItems.find(
                              (item) => item.id === addToCartModalData.editingItemId
                            )
                          : undefined,
                      )}
                    />
                    <div className="mt-1 text-primary">
                      <i className="ri-ruler-line me-1"></i>
                      {addToCartModalData.product.measurement_name}
                    </div>
                    <div className="small text-muted mt-1">
                      Allowed quantity for this project:{" "}
                      {getAllowedQuantity(
                        addToCartModalData.product,
                        addToCartModalData.isEditing
                          ? cartItems.find(
                              (item) => item.id === addToCartModalData.editingItemId
                            )
                          : undefined,
                      ).toFixed(2)}
                    </div>
                    <FormFeedback>{cartFormErrors.quantity}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label
                      htmlFor="expected_delivery_date"
                      className="form-label"
                    >
                      Expected Delivery Date{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      id="expected_delivery_date"
                      name="expected_delivery_date"
                      value={addToCartModalData.expected_delivery_date}
                      onChange={handleAddToCartChange}
                      invalid={!!cartFormErrors.expected_delivery_date}
                      min={moment().format("YYYY-MM-DD")}
                    />
                    <FormFeedback>
                      {cartFormErrors.expected_delivery_date}
                    </FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="delivery_note" className="form-label">
                      Delivery Notes <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      id="delivery_note"
                      name="delivery_note"
                      value={addToCartModalData.delivery_note}
                      onChange={handleAddToCartChange}
                      rows={3}
                      placeholder="Any special delivery instructions"
                      invalid={!!cartFormErrors.delivery_note}
                    />
                    <FormFeedback>{cartFormErrors.delivery_note}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="delivery_area" className="form-label">
                      Delivery Area <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows={3}
                      id="delivery_area"
                      name="delivery_area"
                      value={addToCartModalData.delivery_area}
                      onChange={handleAddToCartChange}
                      placeholder="Enter delivery area"
                      invalid={!!cartFormErrors.delivery_area}
                    />
                    <FormFeedback>{cartFormErrors.delivery_area}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="remark" className="form-label">
                      Remark
                    </Label>
                    <Input
                      type="textarea"
                      rows={3}
                      id="remark"
                      name="remark"
                      value={addToCartModalData.remark}
                      onChange={handleAddToCartChange}
                      placeholder="Enter a remark for the delivery request"
                    />
                    <small className="text-muted">
                      This remark will be shown in the delivery request PDF.
                    </small>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="pr_model_number" className="form-label">
                      PR Model Number
                    </Label>
                    <Input
                      type="text"
                      id="pr_model_number"
                      name="pr_model_number"
                      value={addToCartModalData.pr_model_number}
                      onChange={handleAddToCartChange}
                      placeholder="Enter PR model number"
                      readOnly={!!addToCartModalData.product?.model_number}
                    />
                    {addToCartModalData.product?.model_number && (
                      <small className="text-muted">
                        Synced from the product's model number and cannot be
                        edited.
                      </small>
                    )}
                  </div>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <button
                type="button"
                className="btn btn-light"
                onClick={toggleAddToCartModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddToCartSubmit}
                disabled={isSubmitting || !addToCartModalData.product}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {addToCartModalData.isEditing ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  <>
                    <i className="ri-check-line me-1 align-bottom"></i>
                    {addToCartModalData.isEditing
                      ? "Update Item"
                      : "Add to Cart"}
                  </>
                )}
              </button>
            </ModalFooter>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={deleteConfirmModalOpen}
            toggle={toggleDeleteConfirmModal}
          >
            <ModalHeader toggle={toggleDeleteConfirmModal}>
              Confirm Delete
            </ModalHeader>
            <ModalBody>
              {itemToDelete && (
                <div className="text-center">
                  <i className="ri-delete-bin-line display-4 text-danger mb-3"></i>
                  <h5>Are you sure you want to remove this item?</h5>
                  <p className="text-muted">
                    <strong>{itemToDelete.product.name}</strong> will be removed
                    from your cart.
                  </p>
                  <p className="text-muted small">
                    Quantity: {itemToDelete.quantity}{" "}
                    {itemToDelete.product.measurement_name}
                  </p>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <button
                type="button"
                className="btn btn-light"
                onClick={toggleDeleteConfirmModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteItem}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line me-1 align-bottom"></i>
                    Delete Item
                  </>
                )}
              </button>
            </ModalFooter>
          </Modal>

          {/* ── NEW: PurchaseRequestModal replaces the old inline modal ── */}
          <PurchaseRequestModal
            isOpen={purchaseRequestModalOpen}
            toggle={togglePurchaseRequestModal}
            purchaseRequestData={purchaseRequestData}
            cartItems={cartItems}
            formErrors={formErrors}
            isSubmitting={isSubmitting}
            purchaseId={purchaseId}
            onChange={handlePurchaseRequestChange}
            onSubmit={handleSubmitPurchaseRequest}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PurchaseRequest;
